import withDragAndDrop, { EventInteractionArgs } from "react-big-calendar/lib/addons/dragAndDrop";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "./react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import { MomentFrLocales } from "./momentLocaleFr";
import { useMemo, useState } from "react";
import { CollectionSelectModal } from "./collectionSelectModal";
import { CalendarEventType } from "@/types/bo/calendarType";
import { CollectionEventDropModal } from "./collectionEventDropModal";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import ExpressIcon from "@/assets/icons/express.svg?react";
import { userAtom } from "@/stores/authStore";
import { useAtomValue } from "jotai";
import { Truck, MessageSquareText, UserRound } from "lucide-react";
import { useGetAllExpeditionBo } from "@/api/expedition.api";
import { useGetPickupZones } from "@/api/pickupZones.api";
import { ExpeditionPickupSelectedByEnum } from "@/constants/enums/expeditionPickupSelectedBy.enum";
import { ExpeditionProcessStatusEnum } from "@/constants/enums/expeditionProcessStatus.enum";
import { stripDiacritics } from "@/lib/utils";
import { getExpeditionIdentifier } from "@/lib/expedition.util";

moment.locale("fr", MomentFrLocales);

const matchesExpeditionSearch = (
  expedition: {
    quotation?: { identifier: string } | null;
    customQuotation?: { identifier: string } | null;
    pickupInfo?: { pickupLocation?: { name?: string | null } | null } | null;
  },
  normalizedSearch: string,
): boolean => {
  const locationName = stripDiacritics(
    expedition.pickupInfo?.pickupLocation?.name?.toLowerCase() ?? "",
  );
  if (locationName.includes(normalizedSearch)) return true;

  const quotationId = String(expedition.quotation?.identifier ?? "").toLowerCase();
  const customId = String(expedition.customQuotation?.identifier ?? "").toLowerCase();
  const fullId = (getExpeditionIdentifier(expedition) ?? "").toLowerCase();

  if (fullId.startsWith(normalizedSearch)) return true;

  const prefixed = normalizedSearch.match(/^([cd])-?(\d+)$/);
  if (prefixed) {
    const [, type, digits] = prefixed;
    if (type === "c") return quotationId.startsWith(digits);
    if (type === "d") return customId.startsWith(digits);
  }

  if (/^\d+$/.test(normalizedSearch)) {
    return quotationId.startsWith(normalizedSearch) || customId.startsWith(normalizedSearch);
  }

  return false;
};

const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(Calendar);

type CalendarProps = {
  selectedPickupZoneId: string | null;
  search: string;
};

const formats = {
  eventTimeRangeFormat: () => {
    return "";
  },
};

export const CollectionCalendar = ({ selectedPickupZoneId, search }: CalendarProps) => {
  const { t } = useTranslation("boLogistics");
  const [selectedEvent, setSelectedEvent] = useState<CalendarEventType | null>(null);
  const [eventDropValidationModal, setEventDropValidationModal] =
    useState<EventInteractionArgs<CalendarEventType> | null>(null);
  const [dateRange, setDateRange] = useState<[Date, Date]>(() => {
    const mondayStart = moment().startOf("week").add(0, "days").startOf("day").toDate();
    const fridayEnd = moment().startOf("week").add(4, "days").endOf("day").toDate();
    return [mondayStart, fridayEnd];
  });

  const user = useAtomValue(userAtom);
  const dragAndDropDisabled =
    user?.role !== "ADMIN" && user?.role !== "SUPERADMIN" && user?.role !== "DRIVER";

  const { data: expeditions } = useGetAllExpeditionBo({
    processStatus: [
      ExpeditionProcessStatusEnum.VALIDATED,
      ExpeditionProcessStatusEnum.PENDING_PELICAN,
    ],
    pickupDateAfter: dateRange[0].toISOString(),
    pickupDateBefore: dateRange[1].toISOString(),
    skipPickup: false,
  });
  const { data: pickupZones } = useGetPickupZones();

  const events: CalendarEventType[] = useMemo(() => {
    const eventsTmp: CalendarEventType[] = [];
    const normalizedSearch = stripDiacritics(search.trim().toLowerCase());

    expeditions?.data
      .filter((expedition) =>
        selectedPickupZoneId ? expedition?.pickupInfo?.pickupZoneId === selectedPickupZoneId : true,
      )
      .filter((expedition) =>
        normalizedSearch ? matchesExpeditionSearch(expedition, normalizedSearch) : true,
      )
      .forEach((expedition) => {
        const deliverySummary =
          expedition.quotation?.deliveryContent && expedition.quotation.deliveryContent.length > 0
            ? expedition.quotation.deliveryContent.reduce(
                (result, content) => {
                  const bottles = content.numberOfBottles || 0;
                  result.totalBottles += bottles;
                  return result;
                },
                { totalBottles: 0 },
              )
            : expedition.quotation?.quotationProducts?.reduce(
                (result, product) => {
                  const bottles = product.bottleQuantity || 0;
                  result.totalBottles += bottles;
                  return result;
                },
                { totalBottles: 0 },
              ) || { totalBottles: 0 };

        if (!expedition.pickupInfo || !expedition.pickupDate) return;

        const identifier = getExpeditionIdentifier(expedition) ?? "";

        const description = expedition.quotation
          ? [t("calendar.bottlesCount", { bottles: deliverySummary?.totalBottles ?? 0 })]
          : [t("calendar.customQuotation")];

        eventsTmp.push({
          expeditionId: expedition.id,
          title: identifier,
          description: description,
          comment: expedition.deliveryInfo?.comment,
          start: new Date(new Date(expedition.pickupDate).setHours(12, 0, 0, 0)),
          end: new Date(new Date(expedition.pickupDate).setHours(13, 0, 0, 0)),
          resourceId: !expedition?.pickupInfo?.pickupSlot
            ? 0
            : expedition.pickupInfo?.pickupSlot === "MORNING"
              ? 1
              : 2,
          allDay: true,
          pickupInfo: expedition.pickupInfo,
          isSeparator: false,
          deliveryFromDate: expedition.deliveryInfo?.deliveryFromDate || null,
          express: expedition.expeditionOptions?.express || false,
          companyId: expedition.company.id,
        });
      });

    // Single sort with compound comparison - best practice
    const sortedEvents = eventsTmp.sort((a, b) => {
      // 1. First priority: Sort by slot (morning/afternoon/unspecified)
      const slotOrderA =
        a.pickupInfo.pickupSlot === null ? 0 : a.pickupInfo.pickupSlot === "MORNING" ? 1 : 2;
      const slotOrderB =
        b.pickupInfo.pickupSlot === null ? 0 : b.pickupInfo.pickupSlot === "MORNING" ? 1 : 2;

      if (slotOrderA !== slotOrderB) {
        return slotOrderA - slotOrderB;
      }

      // 2. Second priority: Sort by pickup location
      const zoneA = pickupZones?.find((zone) => zone.id === a.pickupInfo.pickupZoneId)?.name || "";
      const zoneB = pickupZones?.find((zone) => zone.id === b.pickupInfo.pickupZoneId)?.name || "";

      if (zoneA !== zoneB) {
        return zoneA.localeCompare(zoneB);
      }

      // 3. Second priority: Sort by pickup location
      const locationA = a.pickupInfo.pickupLocation?.name || "";
      const locationB = b.pickupInfo.pickupLocation?.name || "";

      if (locationA !== locationB) {
        return locationA.localeCompare(locationB);
      }

      // 4. Third priority: Sort by cotation number
      const cotationNumberA = parseInt(a.title.split("-")[1]?.split(" ")[0] || "0");
      const cotationNumberB = parseInt(b.title.split("-")[1]?.split(" ")[0] || "0");

      if (!isNaN(cotationNumberA) && !isNaN(cotationNumberB)) {
        return cotationNumberA - cotationNumberB;
      }

      return 0;
    });

    return sortedEvents;
  }, [expeditions, selectedPickupZoneId, pickupZones, search, t]);

  const eventWrapper = (props: any) => {
    const event: CalendarEventType = props?.event;
    return (
      <div className="">
        <div className="flex gap-1">
          <div className="flex w-fit flex-row items-center gap-2 rounded-md border border-gray-300 bg-white px-2 py-0 text-sm font-medium text-gray-900">
            {event.resourceId === 0 ? (
              <span>{t("calendar.slotUnspecified")}</span>
            ) : event.resourceId === 1 ? (
              <span>{t("calendar.slotMorning")}</span>
            ) : (
              <span>{t("calendar.slotAfternoon")}</span>
            )}
            {event.pickupInfo.pickupSelectedBy === ExpeditionPickupSelectedByEnum.CLIENT && (
              <UserRound width={16} height={16} />
            )}
            {event.pickupInfo.pickupSelectedBy === ExpeditionPickupSelectedByEnum.DRIVER && (
              <Truck width={16} height={16} />
            )}
            {event.comment && <MessageSquareText width={16} height={16} />}
          </div>
          {event.deliveryFromDate && (
            <div className="py-1/2 w-fit self-center rounded-3xl bg-red-400 px-2.5 text-white">
              {t("calendar.deliveryFromDateBadge")}
            </div>
          )}
          {event.express && (
            <div
              className={`bg-brand600 flex items-center justify-center rounded-full px-2 py-1.5`}
            >
              <ExpressIcon />
            </div>
          )}
        </div>
        {props.children}
        <div className="text-sm font-medium text-gray-900">{event.title}</div>
        {event.description.map((description, index) => (
          <div key={index} className="text-sm text-gray-600">
            {!event?.pickupInfo.pickupLocation ? (
              <p className="truncate">{`${event?.pickupInfo.pickupStreet}, ${event?.pickupInfo.pickupPostalCode} ${event?.pickupInfo.pickupCity}`}</p>
            ) : (
              <p className="truncate">{`${event?.pickupInfo.pickupLocation?.name} - ${event?.pickupInfo.pickupLocation?.street}, ${event?.pickupInfo.pickupLocation?.postalCode} ${event?.pickupInfo.pickupLocation?.city}`}</p>
            )}
            <p>{description}</p>
          </div>
        ))}
      </div>
    );
  };

  const handleEventSelect = (event: any) => {
    setSelectedEvent(event);
  };

  const handleEventDrop = async (args: any) => {
    if (dragAndDropDisabled) {
      toast.error(t("calendar.dragNotAllowed"));
      return;
    }
    setEventDropValidationModal(args);
  };

  const handleDateRange = (args: any) => {
    const startDate = new Date(args[0]);
    const endDate = new Date(new Date(args[args.length - 1]).setHours(23, 59, 59, 999));
    setDateRange([startDate, endDate]);
  };

  const handleRecoloring = (event: any) => {
    const color = event?.pickupInfo?.pickupLocationColor || "#F9FAFB";
    const bgColor = ["#f8e5e5", "#f0f0f2", "#adaaaa"];
    return {
      style: {
        backgroundColor: bgColor[event?.resourceId],
        borderLeft: `8px solid ${color}`,
        marginBottom: "2px",
      },
    };
  };

  return (
    <div className="h-full overflow-hidden">
      <DnDCalendar
        localizer={localizer}
        events={events}
        style={{
          height: "100%",
          width: "100%",
        }}
        components={{
          work_week: {
            event: eventWrapper,
          },
          day: {
            event: eventWrapper,
          },
        }}
        views={["day", "work_week"]}
        defaultView="work_week"
        defaultDate={new Date()}
        formats={formats}
        onRangeChange={(args) => handleDateRange(args)}
        onEventDrop={(args) => handleEventDrop(args)}
        onSelectEvent={(args) => handleEventSelect(args)}
        messages={{
          work_week: t("calendarToolbar.week"),
          day: t("calendarToolbar.day"),
          next: t("calendarToolbar.next"),
          today: t("calendarToolbar.today"),
          previous: t("calendarToolbar.previous"),
        }}
        eventPropGetter={handleRecoloring}
      />
      {selectedEvent && (
        <CollectionSelectModal closeModal={() => setSelectedEvent(null)} event={selectedEvent} />
      )}
      {eventDropValidationModal && (
        <CollectionEventDropModal
          closeModal={() => setEventDropValidationModal(null)}
          dropEvent={eventDropValidationModal}
        />
      )}
    </div>
  );
};
